import React, { useState } from 'react';
import { formatDate } from '@mysimpledb/shared/utils/formatDate';
import {
  handleNextPage,
  handlePrevPage,
} from '@mysimpledb/shared/hooks/usePaginationHelpers';
import {
  closestCenter,
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  NOTE_CATEGORY_COLORS,
  DEFAULT_NOTE_CATEGORY_COLOR,
  getNoteCategoryColor,
} from '@mysimpledb/shared/constants/noteCategoryColors';
import './ListNotesWeb.css';


const LIST_TITLE_ALLOWED_TAGS = new Set([
  'B',
  'STRONG',
  'I',
  'EM',
  'U',
  'S',
  'STRIKE',
]);

function escapeListHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function sanitizeListTitleHtml(value) {
  if (!value) return '';
  if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
    return escapeListHtml(value);
  }

  const doc = new DOMParser().parseFromString(`<div>${value}</div>`, 'text/html');
  const root = doc.body.firstElementChild;
  if (!root) return '';

  Array.from(root.querySelectorAll('*')).forEach((element) => {
    if (element.tagName === 'BR') {
      element.replaceWith(doc.createTextNode(' '));
      return;
    }

    if (element.tagName === 'DIV' || element.tagName === 'P') {
      element.appendChild(doc.createTextNode(' '));
      element.replaceWith(...Array.from(element.childNodes));
      return;
    }

    if (!LIST_TITLE_ALLOWED_TAGS.has(element.tagName)) {
      element.replaceWith(...Array.from(element.childNodes));
      return;
    }
    Array.from(element.attributes).forEach((attr) => element.removeAttribute(attr.name));
  });

  return root.innerHTML.replace(/\s{2,}/g, ' ').trim();
}

function categoryStyle(categoryOrKey) {
  const key =
    typeof categoryOrKey === 'string'
      ? categoryOrKey
      : categoryOrKey?.color_key || DEFAULT_NOTE_CATEGORY_COLOR;
  const color = getNoteCategoryColor(key);

  return {
    '--category-background': color.background,
    '--category-border': color.border,
    '--category-text': color.text,
  };
}

function SortableNoteRow({
  note,
  currentIndex,
  notes,
  listPageClicked,
  listPageNo,
  handleListClick,
  handleDeleteClick,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: String(note.id) });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`list-item ${isDragging ? 'is-dragging' : ''}`}
      onClick={() => handleListClick(note.id)}
    >
      <span
        className="list-col drag-col"
        {...attributes}
        {...listeners}
        onClick={(e) => e.stopPropagation()}
        title="Drag to reorder"
        aria-label="Drag to reorder"
      >
        ⋮⋮
      </span>

      <div className="list-col dot-col">
        {note.id === notes[currentIndex]?.id &&
          listPageClicked === listPageNo && <span className="entry-dot" />}
      </div>

      <span
        className="list-col title item"
        dangerouslySetInnerHTML={{
          __html:
            note.title_html && note.title_html.trim()
              ? sanitizeListTitleHtml(note.title_html)
              : escapeListHtml(note.title || '—'),
        }}
      />

      <span
        className="list-col expanded item expanded-cell"
        title={note.display_mode === 'expanded' ? 'Expanded Note' : ''}
        aria-label={note.display_mode === 'expanded' ? 'Expanded Note' : 'Normal Note'}
      >
        {note.display_mode === 'expanded' ? '✓' : ''}
      </span>

      <span
        className="list-col categories item notes-list-category-cell"
        title={(note.categories || [])
          .map((category) => category.name)
          .join(', ')}
      >
        {(note.categories || []).length
          ? note.categories.map((category) => {
              const primaryId =
                note.primary_category_id != null
                  ? Number(note.primary_category_id)
                  : note.primaryCategoryId != null
                    ? Number(note.primaryCategoryId)
                    : Number(note.categories[0]?.id);
              const isPrimary = Number(category.id) === primaryId;

              return (
                <span
                  key={category.id}
                  className={`notes-list-category-chip ${isPrimary ? 'is-primary' : ''}`}
                  style={categoryStyle(category)}
                  title={isPrimary ? `${category.name} — Primary category` : category.name}
                >
                  {isPrimary ? (
                    <span className="notes-list-primary-mark" aria-hidden="true">★</span>
                  ) : null}
                  <span>{category.name}</span>
                </span>
              );
            })
          : '—'}
      </span>

      <span className="list-col due-at item">
        {note.due_at ? formatDate(note.due_at) : '—'}
      </span>

      <span className="list-col remind-at item">
        {note.remind_at ? formatDate(note.remind_at) : '—'}
      </span>

      <span className="list-col updated-at item">
        {note.updated_at ? formatDate(note.updated_at) : '—'}
      </span>

      <span className="list-col send-sms item send-sms-cell">
        <input
          type="checkbox"
          checked={!!note.send_sms_reminder}
          readOnly
          tabIndex={-1}
          onClick={(e) => e.stopPropagation()}
        />
      </span>

      <span className="list-col delete-col item">
        <button
          type="button"
          className="note-delete-button"
          onClick={(e) => handleDeleteClick(e, note.id)}
        >
          Done
        </button>
      </span>
    </div>
  );
}

export default function ListNotes({
  currentIndex,
  notes,
  setNotes,
  fetchPage,
  deleteNoteById,
  reorderNotesByIds,
  listPageClicked,
  listPageNo,
  setActiveTab,
  setCurrentIndex,
  setCurrentNoteId,
  setForm,
  setIsEditing,
  setListPageClicked,
  setListPageNo,
  setNotePageNo,
  totalNotesFound,
  token,
  userId,
  fetchNoteById,
  categories = [],
  selectedCategoryIds = [],
  setSelectedCategoryIds,
  categoryMatchMode = 'any',
  setCategoryMatchMode,
  uncategorizedOnly = false,
  setUncategorizedOnly,
  createCategory,
  renameCategory,
  archiveCategory,
  reorderCategories,
  customViews = [],
  activeViewId = null,
  selectCustomView,
  clearActiveView,
  createCustomView,
  updateCustomView,
  deleteCustomView,
  fetchAllNotesForViewEditor,
}) {
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryColorKey, setNewCategoryColorKey] = useState(
    DEFAULT_NOTE_CATEGORY_COLOR
  );
  const [categoryBusy, setCategoryBusy] = useState(false);
  const [showCreateView, setShowCreateView] = useState(false);
  const [showViewManager, setShowViewManager] = useState(false);
  const [viewBusy, setViewBusy] = useState(false);
  const [editingViewId, setEditingViewId] = useState(null);
  const [newViewName, setNewViewName] = useState('');
  const [newViewType, setNewViewType] = useState('filtered');
  const [newViewCategoryIds, setNewViewCategoryIds] = useState([]);
  const [newViewDisplayMode, setNewViewDisplayMode] = useState('all');
  const [newViewHasReminder, setNewViewHasReminder] = useState(false);
  const [newViewReminderPastDue, setNewViewReminderPastDue] = useState(false);
  const [newViewHasDueDate, setNewViewHasDueDate] = useState(false);
  const [newViewDueDatePastDue, setNewViewDueDatePastDue] = useState(false);
  const [freeformCatalog, setFreeformCatalog] = useState([]);
  const [freeformNoteIds, setFreeformNoteIds] = useState([]);
  const [freeformLoading, setFreeformLoading] = useState(false);
  const [showCategoryPicker, setShowCategoryPicker] = useState(false);
  const [pickerCategoryIds, setPickerCategoryIds] = useState([]);
  const [pickerMatchMode, setPickerMatchMode] = useState('any');

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 6,
      },
    })
  );

  const handleNewNoteClick = () => {
    setForm({
      id: null,
      title: '',
      titleHtml: '',
      detail: '',
      detailHtml: '',
      dueAt: '',
      remindAt: '',
      expiresAt: '',
      sendSmsReminder: false,
      displayMode: 'normal',
      categoryIds: uncategorizedOnly ? [] : selectedCategoryIds.map(Number),
      categories: uncategorizedOnly
        ? []
        : categories.filter((category) =>
            selectedCategoryIds.some((id) => Number(id) === Number(category.id))
          ),
      primaryCategoryId:
        !uncategorizedOnly && selectedCategoryIds.length
          ? Number(selectedCategoryIds[0])
          : null,
      updatedAt: null,
      userId: userId || null,
    });

    setCurrentNoteId?.(null);
    setCurrentIndex(-1);
    setIsEditing?.(true);
    setActiveTab('notes');
  };

  const handleListClick = async (noteId) => {
    const indexOnPage = notes.findIndex((n) => Number(n.id) === Number(noteId));

    if (indexOnPage >= 0) {
      setCurrentIndex(indexOnPage);
      setCurrentNoteId?.(noteId);
      setListPageClicked?.(listPageNo);
      setNotePageNo?.(listPageNo);
    }

    if (fetchNoteById) {
      const note = await fetchNoteById(noteId, token);

      if (note) {
        setForm({
          id: note.id,
          title: note.title || '',
          titleHtml: note.title_html || '',
          detail: note.detail || '',
          detailHtml: note.detail_html || '',
          dueAt: note.due_at || '',
          remindAt: note.remind_at || '',
          expiresAt: note.expires_at || '',
          sendSmsReminder: !!note.send_sms_reminder,
          displayMode: note.display_mode || 'normal',
          categories: Array.isArray(note.categories) ? note.categories : [],
          categoryIds: Array.isArray(note.categories)
            ? note.categories.map((category) => Number(category.id))
            : [],
          primaryCategoryId:
            note.primary_category_id != null
              ? Number(note.primary_category_id)
              : note.primaryCategoryId != null
                ? Number(note.primaryCategoryId)
                : Array.isArray(note.categories) && note.categories.length
                  ? Number(note.categories[0].id)
                  : null,
          updatedAt: note.updated_at || null,
          userId: note.user_id,
        });
      }
    }

    setActiveTab('notes');
  };

  const handleDeleteClick = async (e, noteId) => {
    e.stopPropagation();

    const confirmed = window.confirm('Delete this note?');
    if (!confirmed) return;

    try {
      await deleteNoteById(noteId, token);

      setForm({
        id: null,
        title: '',
        titleHtml: '',
        detail: '',
        detailHtml: '',
        dueAt: '',
        remindAt: '',
        expiresAt: '',
        sendSmsReminder: false,
        displayMode: 'normal',
        updatedAt: null,
        userId: userId || null,
      });

      setCurrentNoteId?.(null);
      setCurrentIndex(0);

      await fetchPage(listPageNo, { resetIndex: false });
    } catch (err) {
      console.error('Failed to delete note:', err);
      alert('Could not delete note.');
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = notes.findIndex((note) => String(note.id) === active.id);
    const newIndex = notes.findIndex((note) => String(note.id) === over.id);

    if (oldIndex < 0 || newIndex < 0) return;

    const reorderedNotes = arrayMove(notes, oldIndex, newIndex);
    const reorderedIds = reorderedNotes.map((note) => note.id);

    const selectedNoteId = notes[currentIndex]?.id;

    setNotes?.(reorderedNotes);

    if (selectedNoteId) {
      const newSelectedIndex = reorderedNotes.findIndex(
        (note) => Number(note.id) === Number(selectedNoteId)
      );

      if (newSelectedIndex >= 0) {
        setCurrentIndex(newSelectedIndex);
      }
    }

    try {
      await reorderNotesByIds(reorderedIds, token);
      await fetchPage(listPageNo, { resetIndex: false });
    } catch (err) {
      console.error('Failed to reorder notes:', err);
      alert('Could not reorder notes.');
      await fetchPage(listPageNo, { resetIndex: false });
    }
  };

  const toggleCategoryFilter = (categoryId) => {
    clearActiveView?.();
    const numericId = Number(categoryId);
    setUncategorizedOnly?.(false);
    setSelectedCategoryIds?.((prev) =>
      prev.some((id) => Number(id) === numericId)
        ? prev.filter((id) => Number(id) !== numericId)
        : [...prev, numericId]
    );
  };

  const showAllNotes = () => {
    clearActiveView?.();
    setSelectedCategoryIds?.([]);
    setUncategorizedOnly?.(false);
  };

  const showUncategorizedNotes = () => {
    clearActiveView?.();
    setSelectedCategoryIds?.([]);
    setUncategorizedOnly?.(true);
  };


  const openCategoryPicker = () => {
    setPickerCategoryIds(selectedCategoryIds.map(Number));
    setPickerMatchMode(categoryMatchMode === 'all' ? 'all' : 'any');
    setShowCategoryPicker(true);
  };

  const togglePickerCategory = (categoryId) => {
    const numericId = Number(categoryId);

    setPickerCategoryIds((prev) =>
      prev.some((id) => Number(id) === numericId)
        ? prev.filter((id) => Number(id) !== numericId)
        : [...prev, numericId]
    );
  };

  const applyCategoryPicker = () => {
    clearActiveView?.();
    setUncategorizedOnly?.(false);
    setSelectedCategoryIds?.(pickerCategoryIds);
    setCategoryMatchMode?.(
      pickerCategoryIds.length > 1 && pickerMatchMode === 'all' ? 'all' : 'any'
    );
    setListPageNo?.(0);
    setNotePageNo?.(0);
    setCurrentIndex?.(0);
    setShowCategoryPicker(false);
  };

  const showAllCategoriesFromPicker = () => {
    clearActiveView?.();
    setUncategorizedOnly?.(false);
    setSelectedCategoryIds?.([]);
    setCategoryMatchMode?.('any');
    setListPageNo?.(0);
    setNotePageNo?.(0);
    setCurrentIndex?.(0);
    setShowCategoryPicker(false);
  };


  const openViewManager = () => {
    setShowCreateView(false);
    setShowViewManager(true);
  };

  const handleDeleteCustomView = async (view) => {
    if (!view?.id || !deleteCustomView || viewBusy) return;

    const confirmed = window.confirm(
      `Delete custom View "${view.name}"? This does not delete any notes.`
    );

    if (!confirmed) return;

    try {
      setViewBusy(true);
      await deleteCustomView(view.id, token);

      if (String(activeViewId) === String(view.id)) {
        selectCustomView?.(null);
      }
    } catch (err) {
      console.error('Failed to delete custom View:', err);
      alert(
        err?.response?.data?.error ||
          err?.message ||
          'Could not delete custom View.'
      );
    } finally {
      setViewBusy(false);
    }
  };

  const loadFreeformCatalog = async () => {
    if (!fetchAllNotesForViewEditor) return [];

    try {
      setFreeformLoading(true);
      const rows = await fetchAllNotesForViewEditor(token);
      const safeRows = Array.isArray(rows) ? rows : [];
      setFreeformCatalog(safeRows);
      return safeRows;
    } catch (err) {
      console.error('Failed to load notes for free-form View:', err);
      alert('Could not load notes for the free-form View editor.');
      return [];
    } finally {
      setFreeformLoading(false);
    }
  };

  const resetViewEditor = () => {
    setEditingViewId(null);
    setNewViewName('');
    setNewViewType('filtered');
    setNewViewCategoryIds([]);
    setNewViewDisplayMode('all');
    setNewViewHasReminder(false);
    setNewViewReminderPastDue(false);
    setNewViewHasDueDate(false);
    setNewViewDueDatePastDue(false);
    setFreeformNoteIds([]);
  };

  const openCreateView = async () => {
    setShowViewManager(false);
    resetViewEditor();
    setNewViewCategoryIds(
      uncategorizedOnly ? [] : selectedCategoryIds.map(Number)
    );
    setShowCreateView(true);
    await loadFreeformCatalog();
  };


  const openEditSpecificView = async (view) => {
    if (!view) return;

    setShowViewManager(false);
    setEditingViewId(String(view.id));
    setNewViewName(view.name || '');
    setNewViewType(view.viewType === 'freeform' ? 'freeform' : 'filtered');
    setNewViewCategoryIds(
      Array.isArray(view.categoryIds) ? view.categoryIds.map(Number) : []
    );
    setNewViewDisplayMode(
      view.displayMode === 'normal' || view.displayMode === 'expanded'
        ? view.displayMode
        : 'all'
    );
    setNewViewHasReminder(!!view.hasReminder);
    setNewViewReminderPastDue(!!view.reminderPastDue);
    setNewViewHasDueDate(!!view.hasDueDate);
    setNewViewDueDatePastDue(!!view.dueDatePastDue);
    setFreeformNoteIds(
      Array.isArray(view.noteIds)
        ? view.noteIds.map(Number)
        : Array.isArray(view.freeformItems)
          ? view.freeformItems
              .map((item) => Number(item?.noteId))
              .filter((id) => Number.isSafeInteger(id) && id > 0)
          : []
    );

    setShowCreateView(true);
    await loadFreeformCatalog();
  };

  const toggleNewViewCategory = (categoryId) => {
    const numericId = Number(categoryId);

    setNewViewCategoryIds((prev) =>
      prev.some((id) => Number(id) === numericId)
        ? prev.filter((id) => Number(id) !== numericId)
        : [...prev, numericId]
    );
  };

  const addFreeformNote = (noteId) => {
    const numericId = Number(noteId);
    if (!Number.isSafeInteger(numericId) || numericId <= 0) return;

    setFreeformNoteIds((prev) =>
      prev.some((id) => Number(id) === numericId)
        ? prev
        : [...prev, numericId]
    );
  };

  const removeFreeformNote = (noteId) => {
    const numericId = Number(noteId);
    setFreeformNoteIds((prev) =>
      prev.filter((id) => Number(id) !== numericId)
    );
  };

  const handleFreeformDrop = (event) => {
    event.preventDefault();
    const noteId = event.dataTransfer.getData('text/plain');
    addFreeformNote(noteId);
  };

  const handleSaveCustomView = async () => {
    const name = newViewName.trim();
    if (!name || viewBusy) return;

    const payload = {
      name,
      viewType: newViewType,
      categoryIds: newViewType === 'filtered' ? newViewCategoryIds : [],
      displayMode: newViewType === 'filtered' ? newViewDisplayMode : 'all',
      hasReminder:
        newViewType === 'filtered'
          ? newViewHasReminder || newViewReminderPastDue
          : false,
      reminderPastDue:
        newViewType === 'filtered' ? newViewReminderPastDue : false,
      hasDueDate:
        newViewType === 'filtered'
          ? newViewHasDueDate || newViewDueDatePastDue
          : false,
      dueDatePastDue:
        newViewType === 'filtered' ? newViewDueDatePastDue : false,
      noteIds: newViewType === 'freeform' ? freeformNoteIds : [],
    };

    try {
      setViewBusy(true);

      let saved = null;

      if (editingViewId) {
        if (!updateCustomView) return;
        saved = await updateCustomView(editingViewId, payload, token);
      } else {
        if (!createCustomView) return;
        saved = await createCustomView(payload, token);
      }

      setShowCreateView(false);

      if (saved?.id != null) {
        selectCustomView?.(saved.id);
      }
    } catch (err) {
      console.error('Failed to save custom View:', err);
      alert(
        err?.response?.data?.error ||
          err?.message ||
          'Could not save custom View.'
      );
    } finally {
      setViewBusy(false);
    }
  };

  const handleCreateCategory = async () => {
    const name = newCategoryName.trim();
    if (!name || !createCategory || categoryBusy) return;

    try {
      setCategoryBusy(true);
      await createCategory(name, newCategoryColorKey, token);
      setNewCategoryName('');
      setNewCategoryColorKey(DEFAULT_NOTE_CATEGORY_COLOR);
    } catch (err) {
      console.error('Failed to create category:', err);
      alert(err?.response?.data?.error || 'Could not create category.');
    } finally {
      setCategoryBusy(false);
    }
  };

  const handleRenameCategory = async (category) => {
    if (!renameCategory || categoryBusy) return;
    const name = window.prompt('Rename category', category.name);
    if (name == null || !name.trim() || name.trim() === category.name) return;

    try {
      setCategoryBusy(true);
      await renameCategory(
        category.id,
        name.trim(),
        category.color_key || DEFAULT_NOTE_CATEGORY_COLOR,
        token
      );
    } catch (err) {
      console.error('Failed to rename category:', err);
      alert(err?.response?.data?.error || 'Could not rename category.');
    } finally {
      setCategoryBusy(false);
    }
  };

  const handleCategoryColorChange = async (category, colorKey) => {
    if (!renameCategory || categoryBusy) return;
    if ((category.color_key || DEFAULT_NOTE_CATEGORY_COLOR) === colorKey)
      return;

    try {
      setCategoryBusy(true);
      await renameCategory(category.id, category.name, colorKey, token);
    } catch (err) {
      console.error('Failed to update category color:', err);
      alert(err?.response?.data?.error || 'Could not update category color.');
    } finally {
      setCategoryBusy(false);
    }
  };

  const handleArchiveCategory = async (category) => {
    if (!archiveCategory || categoryBusy) return;
    if (
      !window.confirm(
        `Archive category "${category.name}"? Notes will keep the relationship if you restore it later.`
      )
    ) {
      return;
    }

    try {
      setCategoryBusy(true);
      await archiveCategory(category.id, token);
    } catch (err) {
      console.error('Failed to archive category:', err);
      alert(err?.response?.data?.error || 'Could not archive category.');
    } finally {
      setCategoryBusy(false);
    }
  };

  const moveCategory = async (categoryId, direction) => {
    if (!reorderCategories || categoryBusy) return;
    const index = categories.findIndex(
      (category) => Number(category.id) === Number(categoryId)
    );
    const nextIndex = index + direction;
    if (index < 0 || nextIndex < 0 || nextIndex >= categories.length) return;

    const reordered = [...categories];
    const [moved] = reordered.splice(index, 1);
    reordered.splice(nextIndex, 0, moved);

    try {
      setCategoryBusy(true);
      await reorderCategories(
        reordered.map((category) => Number(category.id)),
        token
      );
    } catch (err) {
      console.error('Failed to reorder categories:', err);
      alert(err?.response?.data?.error || 'Could not reorder categories.');
    } finally {
      setCategoryBusy(false);
    }
  };

  return (
    <div className="list-outer-wrapper">
      <button
        className="nav-arrow left"
        onClick={() => handlePrevPage(listPageNo, setListPageNo, fetchPage)}
        disabled={listPageNo === 0}
      >
        &larr;
      </button>

      <form className="notes-form">
        <div className="notes-view-toolbar">
          <div className="notes-view-select-wrap">
            <label htmlFor="notes-custom-view-select">View</label>
            <select
              id="notes-custom-view-select"
              value={activeViewId == null ? '' : String(activeViewId)}
              onChange={(e) => selectCustomView?.(e.target.value || null)}
            >
              <option value="">View All</option>
              {customViews.map((view) => (
                <option key={view.id} value={String(view.id)}>
                  {view.name}
                </option>
              ))}
            </select>
          </div>

          <button
            type="button"
            className="notes-view-button"
            onClick={openViewManager}
          >
            Manage Views
          </button>

          <button
            type="button"
            className={`notes-view-button ${
              !activeViewId && (uncategorizedOnly || selectedCategoryIds.length)
                ? 'is-active'
                : ''
            }`}
            onClick={openCategoryPicker}
          >
            Select Categories
          </button>

          <button
            type="button"
            className="notes-view-button"
            onClick={showAllNotes}
            disabled={!activeViewId && !uncategorizedOnly && selectedCategoryIds.length === 0}
          >
            All Categories
          </button>
        </div>

        <div className="list-toolbar">
          <div
            className="notes-category-filters"
            aria-label="Note category filters"
          >
            <button
              type="button"
              className={`notes-category-filter ${
                !uncategorizedOnly && selectedCategoryIds.length === 0
                  ? 'active'
                  : ''
              }`}
              onClick={showAllNotes}
            >
              All
            </button>

            <button
              type="button"
              className={`notes-category-filter ${
                uncategorizedOnly ? 'active' : ''
              }`}
              onClick={showUncategorizedNotes}
            >
              Uncategorized
            </button>

            {categories.map((category) => {
              const selected = selectedCategoryIds.some(
                (id) => Number(id) === Number(category.id)
              );

              return (
                <button
                  key={category.id}
                  type="button"
                  className={`notes-category-filter notes-category-filter--colored ${
                    selected ? 'active' : ''
                  }`}
                  style={categoryStyle(category)}
                  onClick={() => toggleCategoryFilter(category.id)}
                  title={`${category.note_count ?? 0} note${
                    Number(category.note_count) === 1 ? '' : 's'
                  }`}
                >
                  {category.name}
                  <span className="notes-category-count">
                    {category.note_count ?? 0}
                  </span>
                </button>
              );
            })}

            {selectedCategoryIds.length > 1 && !uncategorizedOnly ? (
              <div
                className="notes-category-match-mode"
                role="group"
                aria-label="Category match mode"
              >
                <button
                  type="button"
                  className={categoryMatchMode === 'any' ? 'active' : ''}
                  onClick={() => setCategoryMatchMode?.('any')}
                  title="Show notes in any selected category"
                >
                  Any
                </button>
                <button
                  type="button"
                  className={categoryMatchMode === 'all' ? 'active' : ''}
                  onClick={() => setCategoryMatchMode?.('all')}
                  title="Show notes in all selected categories"
                >
                  All selected
                </button>
              </div>
            ) : null}
          </div>

          <div className="list-toolbar-actions">
            <button
              type="button"
              className="manage-categories-button"
              onClick={() => setShowCategoryManager((prev) => !prev)}
            >
              {showCategoryManager ? 'Hide Categories' : 'Manage Categories'}
            </button>
            <button
              type="button"
              className="new-note-button"
              onClick={handleNewNoteClick}
            >
              + New Note
            </button>
          </div>
        </div>

        {showViewManager ? (
          <div className="notes-view-manager">
            <div className="notes-view-manager-heading">
              <div>
                <strong>Manage Custom Views</strong>
                <div className="notes-view-manager-help">
                  Add a new View, edit an existing View, or delete a View.
                </div>
              </div>

              <button
                type="button"
                className="notes-view-manager-close"
                onClick={() => setShowViewManager(false)}
                aria-label="Close View manager"
              >
                ×
              </button>
            </div>

            <div className="notes-view-manager-actions">
              <button
                type="button"
                className="notes-view-manager-add"
                onClick={openCreateView}
              >
                + Add New View
              </button>
            </div>

            <div className="notes-view-manager-list">
              {customViews.length ? (
                customViews.map((view) => (
                  <div
                    key={view.id}
                    className={`notes-view-manager-row ${
                      String(view.id) === String(activeViewId) ? 'is-active' : ''
                    }`}
                  >
                    <div className="notes-view-manager-info">
                      <div className="notes-view-manager-name">
                        {view.name}
                      </div>
                      <div className="notes-view-manager-type">
                        {view.viewType === 'freeform'
                          ? 'Free Form View'
                          : 'Filtered View'}
                        {String(view.id) === String(activeViewId)
                          ? ' • Currently selected'
                          : ''}
                      </div>
                    </div>

                    <div className="notes-view-manager-row-actions">
                      <button
                        type="button"
                        onClick={() => selectCustomView?.(view.id)}
                      >
                        Select
                      </button>

                      <button
                        type="button"
                        onClick={() => openEditSpecificView(view)}
                      >
                        Edit
                      </button>

                      <button
                        type="button"
                        className="notes-view-manager-delete"
                        onClick={() => handleDeleteCustomView(view)}
                        disabled={viewBusy}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="notes-view-manager-empty">
                  No custom Views have been created yet.
                </div>
              )}
            </div>
          </div>
        ) : null}

        {showCategoryPicker ? (
          <div className="notes-category-picker-panel">
            <div className="notes-category-picker-heading">
              <div>
                <strong>Select Categories</strong>
                <div className="notes-category-picker-help">
                  Choose one or more categories, then click Apply.
                </div>
              </div>

              <button
                type="button"
                className="notes-category-picker-close"
                onClick={() => setShowCategoryPicker(false)}
                aria-label="Close category picker"
              >
                ×
              </button>
            </div>

            <div className="notes-category-picker-options">
              {categories.length ? (
                categories.map((category) => {
                  const selected = pickerCategoryIds.some(
                    (id) => Number(id) === Number(category.id)
                  );

                  return (
                    <label
                      key={category.id}
                      className={`notes-category-picker-option ${
                        selected ? 'selected' : ''
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={() => togglePickerCategory(category.id)}
                      />
                      <span
                        className="notes-category-picker-swatch"
                        style={categoryStyle(category)}
                        aria-hidden="true"
                      />
                      <span className="notes-category-picker-name">
                        {category.name}
                      </span>
                    </label>
                  );
                })
              ) : (
                <span className="notes-view-empty">No categories.</span>
              )}
            </div>

            {pickerCategoryIds.length > 1 ? (
              <div className="notes-category-picker-match">
                <span>Match:</span>
                <label>
                  <input
                    type="radio"
                    name="category-picker-match"
                    value="any"
                    checked={pickerMatchMode === 'any'}
                    onChange={() => setPickerMatchMode('any')}
                  />
                  Any selected category
                </label>
                <label>
                  <input
                    type="radio"
                    name="category-picker-match"
                    value="all"
                    checked={pickerMatchMode === 'all'}
                    onChange={() => setPickerMatchMode('all')}
                  />
                  All selected categories
                </label>
              </div>
            ) : null}

            <div className="notes-category-picker-actions">
              <button
                type="button"
                onClick={showAllCategoriesFromPicker}
              >
                All Categories
              </button>

              <div className="notes-category-picker-actions-right">
                <button
                  type="button"
                  onClick={() => setShowCategoryPicker(false)}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  className="notes-category-picker-apply"
                  onClick={applyCategoryPicker}
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {showCreateView ? (
          <div className="notes-view-editor">
            <div className="notes-view-editor-heading">
              <strong>
                {editingViewId ? 'Edit Custom View' : 'Create Custom View'}
              </strong>
              <button
                type="button"
                className="notes-view-editor-close"
                onClick={() => setShowCreateView(false)}
                aria-label="Close custom View editor"
              >
                ×
              </button>
            </div>

            <div className="notes-view-editor-grid">
              <label className="notes-view-field">
                <span>View name</span>
                <input
                  type="text"
                  maxLength={50}
                  value={newViewName}
                  onChange={(e) => setNewViewName(e.target.value)}
                  placeholder="Example: Medical reminders"
                  autoFocus
                />
              </label>

              <label className="notes-view-field">
                <span>View type</span>
                <select
                  value={newViewType}
                  onChange={(e) => setNewViewType(e.target.value)}
                >
                  <option value="filtered">Filtered View</option>
                  <option value="freeform">Free Form View</option>
                </select>
              </label>

              {newViewType === 'filtered' ? (
                <label className="notes-view-field">
                  <span>Display</span>
                  <select
                    value={newViewDisplayMode}
                    onChange={(e) => setNewViewDisplayMode(e.target.value)}
                  >
                    <option value="all">All notes</option>
                    <option value="normal">Regular notes only</option>
                    <option value="expanded">Expanded notes only</option>
                  </select>
                </label>
              ) : null}
            </div>

            {newViewType === 'filtered' ? (
              <>
                <div className="notes-view-section">
                  <div className="notes-view-section-title">Categories</div>
                  <div className="notes-view-category-picker">
                    {categories.length ? (
                      categories.map((category) => {
                        const selected = newViewCategoryIds.some(
                          (id) => Number(id) === Number(category.id)
                        );

                        return (
                          <label
                            key={category.id}
                            className={`notes-view-category-option ${
                              selected ? 'selected' : ''
                            }`}
                            style={categoryStyle(category)}
                          >
                            <input
                              type="checkbox"
                              checked={selected}
                              onChange={() => toggleNewViewCategory(category.id)}
                            />
                            <span>{category.name}</span>
                          </label>
                        );
                      })
                    ) : (
                      <span className="notes-view-empty">No categories.</span>
                    )}
                  </div>
                </div>

                <div className="notes-view-section">
                  <div className="notes-view-section-title">Other filters</div>
                  <div className="notes-view-checks">
                    <label>
                      <input
                        type="checkbox"
                        checked={newViewHasReminder}
                        onChange={(e) => setNewViewHasReminder(e.target.checked)}
                      />
                      Has reminder
                    </label>

                    <label>
                      <input
                        type="checkbox"
                        checked={newViewReminderPastDue}
                        onChange={(e) => setNewViewReminderPastDue(e.target.checked)}
                      />
                      Reminder past due
                    </label>

                    <label>
                      <input
                        type="checkbox"
                        checked={newViewHasDueDate}
                        onChange={(e) => setNewViewHasDueDate(e.target.checked)}
                      />
                      Has due date
                    </label>

                    <label>
                      <input
                        type="checkbox"
                        checked={newViewDueDatePastDue}
                        onChange={(e) => setNewViewDueDatePastDue(e.target.checked)}
                      />
                      Due date past due
                    </label>
                  </div>
                </div>
              </>
            ) : (
              <div className="notes-freeform-editor">
                <div className="notes-freeform-help">
                  Drag notes into the View, or click Add. This selects which notes
                  belong to the View; it does not change their normal layout.
                </div>

                <div className="notes-freeform-columns">
                  <div className="notes-freeform-column">
                    <div className="notes-freeform-column-title">Available Notes</div>
                    <div className="notes-freeform-list">
                      {freeformLoading ? (
                        <div className="notes-view-empty">Loading notes…</div>
                      ) : (
                        freeformCatalog
                          .filter(
                            (note) =>
                              !freeformNoteIds.some(
                                (id) => Number(id) === Number(note.id)
                              )
                          )
                          .map((note) => (
                            <div
                              key={note.id}
                              className="notes-freeform-note"
                              style={categoryStyle(note.categories?.[0])}
                              draggable
                              onDragStart={(event) =>
                                event.dataTransfer.setData(
                                  'text/plain',
                                  String(note.id)
                                )
                              }
                            >
                              <span className="notes-freeform-note-title">
                                {note.title || 'Untitled'}
                                {note.display_mode === 'expanded'
                                  ? ' (Expanded)'
                                  : ''}
                              </span>
                              <button
                                type="button"
                                onClick={() => addFreeformNote(note.id)}
                              >
                                Add
                              </button>
                            </div>
                          ))
                      )}
                    </div>
                  </div>

                  <div
                    className="notes-freeform-column notes-freeform-dropzone"
                    onDragOver={(event) => event.preventDefault()}
                    onDrop={handleFreeformDrop}
                  >
                    <div className="notes-freeform-column-title">
                      Notes In This View
                    </div>

                    <div className="notes-freeform-list">
                      {freeformNoteIds.length ? (
                        freeformNoteIds.map((noteId) => {
                          const note = freeformCatalog.find(
                            (candidate) =>
                              Number(candidate.id) === Number(noteId)
                          );

                          return (
                            <div
                              key={noteId}
                              className="notes-freeform-note is-selected"
                              style={categoryStyle(note?.categories?.[0])}
                            >
                              <span className="notes-freeform-note-title">
                                {note?.title || `Note ${noteId}`}
                                {note?.display_mode === 'expanded'
                                  ? ' (Expanded)'
                                  : ''}
                              </span>
                              <button
                                type="button"
                                className="remove"
                                onClick={() => removeFreeformNote(noteId)}
                              >
                                Remove
                              </button>
                            </div>
                          );
                        })
                      ) : (
                        <div className="notes-freeform-empty">
                          Drag notes here or click Add.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="notes-view-editor-actions">
              <button
                type="button"
                onClick={() => setShowCreateView(false)}
                disabled={viewBusy}
              >
                Cancel
              </button>

              <button
                type="button"
                className="notes-view-save-button"
                onClick={handleSaveCustomView}
                disabled={
                  !newViewName.trim() ||
                  viewBusy ||
                  (newViewType === 'freeform' && freeformNoteIds.length === 0)
                }
              >
                {viewBusy
                  ? 'Saving…'
                  : editingViewId
                    ? 'Save Changes'
                    : 'Save View'}
              </button>
            </div>
          </div>
        ) : null}

        {showCategoryManager ? (
          <div className="notes-category-manager">
            <div className="notes-category-manager-add">
              <input
                type="text"
                value={newCategoryName}
                maxLength={100}
                placeholder="New category name"
                onChange={(e) => setNewCategoryName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleCreateCategory();
                  }
                }}
              />
              <div
                className="note-category-color-options"
                aria-label="New category color"
              >
                {NOTE_CATEGORY_COLORS.map((colorKey) => {
                  const selected = newCategoryColorKey === colorKey;
                  const color = getNoteCategoryColor(colorKey);

                  return (
                    <button
                      key={colorKey}
                      type="button"
                      className={`note-category-color-swatch ${
                        selected ? 'is-selected' : ''
                      }`}
                      style={categoryStyle(colorKey)}
                      onClick={() => setNewCategoryColorKey(colorKey)}
                      title={color.label}
                      aria-label={`${color.label} category color`}
                      aria-pressed={selected}
                    ></button>
                  );
                })}
              </div>

              <button
                type="button"
                onClick={handleCreateCategory}
                disabled={!newCategoryName.trim() || categoryBusy}
              >
                Add Category
              </button>
            </div>

            <div className="notes-category-manager-list">
              {categories.length ? (
                categories.map((category, index) => (
                  <div className="notes-category-manager-row" key={category.id}>
                    <div className="notes-category-manager-name">
                      <span
                        className="notes-category-manager-name-chip"
                        style={categoryStyle(category)}
                      >
                        {category.name}
                      </span>
                      <span className="notes-category-manager-count">
                        {category.note_count ?? 0} notes
                      </span>
                    </div>
                    <div
                      className="note-category-color-options note-category-color-options--compact"
                      aria-label={`${category.name} color`}
                    >
                      {NOTE_CATEGORY_COLORS.map((colorKey) => {
                        const selected =
                          (category.color_key ||
                            DEFAULT_NOTE_CATEGORY_COLOR) === colorKey;
                        const color = getNoteCategoryColor(colorKey);

                        return (
                          <button
                            key={colorKey}
                            type="button"
                            className={`note-category-color-swatch note-category-color-swatch--small ${
                              selected ? 'is-selected' : ''
                            }`}
                            style={categoryStyle(colorKey)}
                            onClick={() =>
                              handleCategoryColorChange(category, colorKey)
                            }
                            disabled={categoryBusy}
                            title={color.label}
                            aria-label={`Set ${category.name} to ${color.label}`}
                            aria-pressed={selected}
                          >
                            {selected ? '✓' : ''}
                          </button>
                        );
                      })}
                    </div>
                    <div className="notes-category-manager-actions">
                      <button
                        type="button"
                        onClick={() => moveCategory(category.id, -1)}
                        disabled={index === 0 || categoryBusy}
                        title="Move category up"
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        onClick={() => moveCategory(category.id, 1)}
                        disabled={
                          index === categories.length - 1 || categoryBusy
                        }
                        title="Move category down"
                      >
                        ↓
                      </button>
                      <button
                        type="button"
                        onClick={() => handleRenameCategory(category)}
                        disabled={categoryBusy}
                      >
                        Rename
                      </button>
                      <button
                        type="button"
                        className="archive-category-button"
                        onClick={() => handleArchiveCategory(category)}
                        disabled={categoryBusy}
                      >
                        Archive
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="notes-category-manager-empty">
                  No categories yet.
                </div>
              )}
            </div>
          </div>
        ) : null}

        <div className="list-table-wrap">
          <div className="list-header">
            <span className="list-col drag-col heading" />
            <span className="list-col dot-col" />
            <span className="list-col title heading">Title</span>
            <span className="list-col expanded heading">Exp</span>
            <span className="list-col categories heading">Categories</span>
            <span className="list-col due-at heading">Due</span>
            <span className="list-col remind-at heading">Reminder</span>
            <span className="list-col updated-at heading">Updated</span>
            <span className="list-col send-sms heading">Send SMS</span>
            <span className="list-col delete-col heading">Done</span>
          </div>

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={notes.map((note) => String(note.id))}
              strategy={verticalListSortingStrategy}
            >
              {notes.map((note) => (
                <SortableNoteRow
                  key={note.id}
                  note={note}
                  currentIndex={currentIndex}
                  notes={notes}
                  listPageClicked={listPageClicked}
                  listPageNo={listPageNo}
                  handleListClick={handleListClick}
                  handleDeleteClick={handleDeleteClick}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>
      </form>

      <button
        className="nav-arrow right"
        onClick={() => handleNextPage(listPageNo, setListPageNo, fetchPage)}
        disabled={listPageNo + 1 === Math.ceil(totalNotesFound / 30)}
      >
        &rarr;
      </button>

      <span className="entry-counter">
        {totalNotesFound > 0
          ? `${listPageNo + 1} of ${Math.ceil(totalNotesFound / 30)}`
          : '0 of 0'}
      </span>
    </div>
  );
}
